import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, Download, LogOut, ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError, resolveMedia } from "../../lib/api";
import { useSEO } from "../../lib/useSEO";

const inputCls = "w-full py-3 px-4 bg-obsidian border border-gold/20 text-cream text-sm outline-none focus:border-gold placeholder:text-sand/40";
const labelCls = "block font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70 mb-2";

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const TextField = ({ label, value, onChange, testid, placeholder }) => (
  <Field label={label}>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={inputCls} />
  </Field>
);

const TextArea = ({ label, value, onChange, testid, rows = 3 }) => (
  <Field label={label}>
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid} className={`${inputCls} resize-y`} />
  </Field>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`flex items-center gap-3 px-4 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors ${
      checked ? "border-gold bg-gold/15 text-gold" : "border-gold/20 text-sand hover:border-gold/50"
    }`}
  >
    <span className={`w-2 h-2 rounded-full ${checked ? "bg-gold" : "bg-sand/30"}`} />
    {label}: {checked ? "Yes" : "No"}
  </button>
);

const UploadField = ({ label, value, onChange, accept, testid, isImage }) => {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success(`${f.name} uploaded — now click Save below to attach it`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };
  return (
    <Field label={label}>
      <div className="border border-dashed border-gold/25 p-4">
        {value && isImage && <img src={resolveMedia(value)} alt="" className="w-24 h-24 object-cover mb-3 border border-gold/20" />}
        {value && !isImage && <p className="text-xs text-gold mb-3 break-all">{value.split("/").pop()}</p>}
        <label className={`inline-flex items-center gap-2 px-4 py-2.5 border border-gold/40 text-gold font-mono text-[10px] tracking-[0.25em] uppercase cursor-pointer hover:bg-gold hover:text-obsidian transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <Upload size={13} /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}
          <input type="file" accept={accept} onChange={handle} className="hidden" data-testid={testid} />
        </label>
      </div>
    </Field>
  );
};

const EMPTY_VAULT = {
  title: "", slug: "", category: "Royal Vault", release_date: "", description: "",
  story: "", lyrics: "", artwork_url: "", audio_url: "", voice_note_url: "", video_url: "",
  featured: false, published: false,
};
const EMPTY_OFFICIAL = {
  title: "", artwork_url: "", release_date: "", description: "", audio_url: "", video_url: "",
  streaming_links: {}, lyrics: "", credits: "", status: "upcoming", featured: false, published: true,
};
const EMPTY_CONTENT = {
  title: "", type: "Announcement", image_url: "", video_url: "", link_url: "",
  description: "", date: "", featured: false, published: true,
};

function CollectionTab({ endpoint, empty, fields, testPrefix, extraRow, onPreview }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [endpoint]);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/${endpoint}/${editing.id}`, editing);
        toast.success("Saved");
      } else {
        await api.post(`/admin/${endpoint}`, editing);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const quickToggle = async (item, key) => {
    try {
      await api.put(`/admin/${endpoint}/${item.id}`, { [key]: !item[key] });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/${endpoint}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (k) => (v) => setEditing((prev) => ({ ...prev, [k]: v }));

  return (
    <div>
      {!editing && (
        <button
          onClick={() => setEditing({ ...empty })}
          data-testid={`${testPrefix}-new-btn`}
          className="mb-6 inline-flex items-center gap-2 h-12 px-6 bg-gold text-obsidian font-mono text-[10px] tracking-[0.25em] uppercase hover:bg-goldglow transition-colors"
        >
          <Plus size={14} /> Add New
        </button>
      )}

      {editing && (
        <div className="mb-8 border border-gold/30 bg-gallery p-6 sm:p-8" data-testid={`${testPrefix}-form`}>
          <p className="font-serif text-2xl text-cream mb-6">{editing.id ? "Edit Entry" : "New Entry"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {fields(set, editing).map((f, i) => <div key={i} className={f.wide ? "sm:col-span-2" : ""}>{f.node}</div>)}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={save} data-testid={`${testPrefix}-save-btn`} className="h-12 px-8 bg-gold text-obsidian font-mono text-[10px] tracking-[0.25em] uppercase hover:bg-goldglow transition-colors">
              Save
            </button>
            <button onClick={() => setEditing(null)} data-testid={`${testPrefix}-cancel-btn`} className="h-12 px-8 border border-gold/30 text-sand font-mono text-[10px] tracking-[0.25em] uppercase hover:border-gold hover:text-cream transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!items ? (
        <p className="text-sand text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sand text-sm">Nothing here yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} data-testid={`${testPrefix}-row-${item.slug || item.id}`} className="border border-gold/15 bg-gallery/60 p-4 flex flex-wrap items-center gap-4">
              {item.artwork_url || item.image_url ? (
                <img src={resolveMedia(item.artwork_url || item.image_url)} alt="" className="w-14 h-14 object-cover border border-gold/20" />
              ) : (
                <div className="w-14 h-14 bg-velvet border border-gold/20" />
              )}
              <div className="flex-1 min-w-[160px]">
                <p className="font-serif text-lg text-cream">{item.title}</p>
                <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-sand/60">
                  {item.category || item.type || item.status || ""} {extraRow ? extraRow(item) : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {item.featured && <span className="px-2.5 py-1 bg-gold/15 text-gold font-mono text-[9px] tracking-[0.2em] uppercase">Featured</span>}
                {!item.published && <span className="px-2.5 py-1 bg-wine/30 text-cream/80 font-mono text-[9px] tracking-[0.2em] uppercase">Draft</span>}
                <button onClick={() => quickToggle(item, "featured")} title="Toggle featured" data-testid={`${testPrefix}-feature-${item.slug || item.id}`} className={`p-2.5 border transition-colors ${item.featured ? "border-gold text-gold" : "border-gold/20 text-sand/50 hover:text-gold"}`}>
                  <Star size={14} />
                </button>
                <button onClick={() => quickToggle(item, "published")} title="Toggle published" data-testid={`${testPrefix}-publish-${item.slug || item.id}`} className={`p-2.5 border transition-colors ${item.published ? "border-gold/40 text-gold" : "border-gold/20 text-sand/50 hover:text-gold"}`}>
                  {item.published ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                {onPreview && item.published && (
                  <a href={onPreview(item)} target="_blank" rel="noopener noreferrer" title="Preview" data-testid={`${testPrefix}-preview-${item.slug || item.id}`} className="p-2.5 border border-gold/20 text-sand/50 hover:text-gold transition-colors">
                    <ExternalLink size={14} />
                  </a>
                )}
                <button onClick={() => setEditing({ ...item })} title="Edit" data-testid={`${testPrefix}-edit-${item.slug || item.id}`} className="p-2.5 border border-gold/20 text-sand/50 hover:text-gold transition-colors">
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(item)} title="Delete" data-testid={`${testPrefix}-delete-${item.slug || item.id}`} className="p-2.5 border border-gold/20 text-sand/50 hover:text-wineaccent hover:border-wineaccent transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const vaultFields = (set, v) => [
  { node: <TextField label="Title" value={v.title} onChange={set("title")} testid="vault-form-title" /> },
  { node: <TextField label="URL Slug (auto if blank)" value={v.slug} onChange={set("slug")} testid="vault-form-slug" placeholder="bounce-back" /> },
  { node: <SelectField label="Category" value={v.category} onChange={set("category")} testid="vault-form-category" options={["Royal Vault", "Royal Vault / Social Audio", "Voice Note", "Interlude", "Demo", "Spoken Word", "Archive Selection"]} /> },
  { node: <TextField label="Release Date" value={v.release_date} onChange={set("release_date")} testid="vault-form-date" placeholder="2026-07-01" /> },
  { node: <TextArea label="Short Description" value={v.description} onChange={set("description")} testid="vault-form-description" rows={2} />, wide: true },
  { node: <TextArea label="Story / Liner Notes" value={v.story} onChange={set("story")} testid="vault-form-story" rows={4} />, wide: true },
  { node: <TextArea label="Lyrics" value={v.lyrics} onChange={set("lyrics")} testid="vault-form-lyrics" rows={4} />, wide: true },
  { node: <UploadField label="Cover Artwork" value={v.artwork_url} onChange={set("artwork_url")} accept="image/*" isImage testid="vault-form-artwork" /> },
  { node: <UploadField label="Audio" value={v.audio_url} onChange={set("audio_url")} accept="audio/*" testid="vault-form-audio" /> },
  { node: <UploadField label="Original Voice Note (optional)" value={v.voice_note_url} onChange={set("voice_note_url")} accept="audio/*" testid="vault-form-voicenote" /> },
  { node: <TextField label="Video URL (optional embed)" value={v.video_url} onChange={set("video_url")} testid="vault-form-video" /> },
  { node: <Toggle label="Sell as download" checked={v.for_sale} onChange={set("for_sale")} testid="vault-form-forsale" /> },
  { node: <TextField label="Download Price (USD)" value={v.price} onChange={set("price")} testid="vault-form-price" placeholder="1.99" /> },
  { node: <Toggle label="Featured on homepage" checked={v.featured} onChange={set("featured")} testid="vault-form-featured" /> },
  { node: <Toggle label="Published" checked={v.published} onChange={set("published")} testid="vault-form-published" /> },
];

const officialFields = (set, v) => [
  { node: <TextField label="Title" value={v.title} onChange={set("title")} testid="official-form-title" /> },
  { node: <SelectField label="Status" value={v.status} onChange={set("status")} testid="official-form-status" options={["upcoming", "released"]} /> },
  { node: <TextField label="Release Date" value={v.release_date} onChange={set("release_date")} testid="official-form-date" placeholder="2026-09-01" /> },
  { node: <UploadField label="Artwork" value={v.artwork_url} onChange={set("artwork_url")} accept="image/*" isImage testid="official-form-artwork" /> },
  { node: <TextArea label="Description" value={v.description} onChange={set("description")} testid="official-form-description" rows={3} />, wide: true },
  { node: <UploadField label="Audio Preview (optional)" value={v.audio_url} onChange={set("audio_url")} accept="audio/*" testid="official-form-audio" /> },
  { node: <TextField label="Video URL (optional)" value={v.video_url} onChange={set("video_url")} testid="official-form-video" /> },
  { node: <TextField label="Spotify Link" value={v.streaming_links?.spotify} onChange={(x) => set("streaming_links")({ ...(v.streaming_links || {}), spotify: x })} testid="official-form-spotify" /> },
  { node: <TextField label="Apple Music Link" value={v.streaming_links?.apple} onChange={(x) => set("streaming_links")({ ...(v.streaming_links || {}), apple: x })} testid="official-form-apple" /> },
  { node: <TextArea label="Lyrics" value={v.lyrics} onChange={set("lyrics")} testid="official-form-lyrics" rows={3} />, wide: true },
  { node: <TextArea label="Credits" value={v.credits} onChange={set("credits")} testid="official-form-credits" rows={3} />, wide: true },
  { node: <Toggle label="Featured" checked={v.featured} onChange={set("featured")} testid="official-form-featured" /> },
  { node: <Toggle label="Published" checked={v.published} onChange={set("published")} testid="official-form-published" /> },
];

const contentFields = (set, v) => [
  { node: <TextField label="Title" value={v.title} onChange={set("title")} testid="content-form-title" /> },
  { node: <SelectField label="Type" value={v.type} onChange={set("type")} testid="content-form-type" options={["Reel", "YouTube Short", "Video", "Performance", "Behind the Scenes", "Royal Vault Moment", "Announcement"]} /> },
  { node: <TextField label="Date" value={v.date} onChange={set("date")} testid="content-form-date" placeholder="2026-07" /> },
  { node: <UploadField label="Image" value={v.image_url} onChange={set("image_url")} accept="image/*" isImage testid="content-form-image" /> },
  { node: <TextField label="Video URL (optional)" value={v.video_url} onChange={set("video_url")} testid="content-form-video" /> },
  { node: <TextField label="Link URL (optional, e.g. /vault/bounce-back)" value={v.link_url} onChange={set("link_url")} testid="content-form-link" /> },
  { node: <TextArea label="Description" value={v.description} onChange={set("description")} testid="content-form-description" rows={3} />, wide: true },
  { node: <Toggle label="Featured" checked={v.featured} onChange={set("featured")} testid="content-form-featured" /> },
  { node: <Toggle label="Published" checked={v.published} onChange={set("published")} testid="content-form-published" /> },
];

function SubscribersTab() {
  const [subs, setSubs] = useState(null);
  useEffect(() => {
    api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "royal-list-subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sand text-sm">{subs ? `${subs.length} subscriber${subs.length === 1 ? "" : "s"}` : "Loading…"}</p>
        <button onClick={exportCsv} data-testid="admin-export-subscribers-btn" className="inline-flex items-center gap-2 h-11 px-6 bg-gold text-obsidian font-mono text-[10px] tracking-[0.25em] uppercase hover:bg-goldglow transition-colors">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="border border-gold/15 overflow-x-auto">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead>
            <tr className="border-b border-gold/15 text-left">
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">First Name</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Email</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Source</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Joined</th>
            </tr>
          </thead>
          <tbody>
            {(subs || []).map((s) => (
              <tr key={s.id} className="border-b border-gold/10 text-sand">
                <td className="px-4 py-3 text-cream">{s.first_name}</td>
                <td className="px-4 py-3">{s.email}</td>
                <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider">{s.source}</td>
                <td className="px-4 py-3 text-xs">{(s.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {subs && subs.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sand/60">No subscribers yet — share the Royal List link.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/activity").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  return (
    <div className="space-y-2" data-testid="activity-log">
      {(items || []).map((a) => (
        <div key={a.id} className="border border-gold/10 bg-gallery/50 px-4 py-3 flex flex-wrap gap-x-4 gap-y-1 items-baseline">
          <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold/70 w-20">{a.action}</span>
          <span className="text-sm text-cream flex-1">{a.detail}</span>
          <span className="font-mono text-[9px] text-sand/50">{(a.created_at || "").replace("T", " ").slice(0, 16)}</span>
        </div>
      ))}
      {items && items.length === 0 && <p className="text-sand text-sm">No activity yet.</p>}
    </div>
  );
}

const SITE_FIELDS = [
  { section: "Site Mode" },
  { key: "archive_mode", label: "Archive Mode — show only the Coming Soon landing page", type: "toggle" },
  { key: "landing_image", label: "Coming Soon Page Background (leave empty for plain black)", type: "image" },
  { key: "landing_portrait", label: "Coming Soon Page Portrait (framed photo above the headline)", type: "image" },
  { section: "Homepage" },
  { key: "hero_image", label: "Hero Image (homepage)", type: "image" },
  { key: "hero_tagline", label: "Hero Tagline", type: "text" },
  { key: "meet_portrait", label: "'Meet Queen Denae' Portrait (homepage)", type: "image" },
  { section: "About Page" },
  { key: "about_image_1", label: "Chapter 1 Image — Who Is Queen Denae", type: "image" },
  { key: "about_image_2", label: "Chapter 2 Image — The Music", type: "image" },
  { key: "about_image_3", label: "Chapter 3 Image — The Woman Behind The Music", type: "image" },
  { key: "about_quote_1", label: "Pull Quote 1", type: "text" },
  { key: "about_quote_2", label: "Pull Quote 2", type: "text" },
  { key: "about_ch1_body", label: "Chapter 1 — Who Is Queen Denae", type: "textarea" },
  { key: "about_ch2_body", label: "Chapter 2 — The Music", type: "textarea" },
  { key: "about_ch3_body", label: "Chapter 3 — The Woman Behind The Music", type: "textarea" },
  { key: "about_ch4_body", label: "Chapter 4 — The Next Chapter", type: "textarea" },
  { section: "Footer & Social" },
  { key: "contact_email", label: "Contact Email", type: "text" },
  { key: "social_instagram", label: "Instagram URL", type: "text" },
  { key: "social_youtube", label: "YouTube URL", type: "text" },
  { key: "social_facebook", label: "Facebook URL", type: "text" },
  { key: "social_tiktok", label: "TikTok URL", type: "text" },
];

function SiteTab() {
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setValues(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  const set = (k) => (v) => setValues((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", values);
      toast.success("Saved — live on the site now");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };
  if (!values) return <p className="text-sand text-sm">Loading…</p>;
  return (
    <div data-testid="admin-site-tab">
      <p className="text-sand text-sm mb-6 max-w-xl">
        Change the site's photography and words. Upload your own images or rewrite any text — everything goes live when you save. No code.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {SITE_FIELDS.map((f) =>
          f.section ? (
            <p key={f.section} className="sm:col-span-2 font-mono text-[10px] tracking-[0.35em] uppercase text-gold mt-4 border-b border-gold/15 pb-2">
              {f.section}
            </p>
          ) : f.type === "image" ? (
            <UploadField key={f.key} label={f.label} value={values[f.key]} onChange={set(f.key)} accept="image/*" isImage testid={`site-${f.key.replace(/_/g, "-")}`} />
          ) : f.type === "toggle" ? (
            <div key={f.key} className="sm:col-span-2">
              <Toggle label={f.label} checked={values[f.key] === "on"} onChange={(v) => set(f.key)(v ? "on" : "off")} testid="site-archive-mode-toggle" />
              <p className="mt-2 text-xs text-sand/50 max-w-lg">
                When on, visitors see only the Coming Soon landing page — the Vault, songs, and all pages are hidden but fully preserved. Turn off anytime to relaunch the full site instantly.
              </p>
            </div>
          ) : f.type === "textarea" ? (
            <div key={f.key} className="sm:col-span-2">
              <TextArea label={f.label} value={values[f.key]} onChange={set(f.key)} rows={4} testid={`site-${f.key.replace(/_/g, "-")}`} />
            </div>
          ) : (
            <TextField key={f.key} label={f.label} value={values[f.key]} onChange={set(f.key)} testid={`site-${f.key.replace(/_/g, "-")}`} />
          )
        )}
      </div>
      <button
        onClick={save}
        disabled={busy}
        data-testid="site-save-btn"
        className="mt-6 h-12 px-8 bg-gold text-obsidian font-mono text-[10px] tracking-[0.25em] uppercase hover:bg-goldglow transition-colors disabled:opacity-60"
      >
        {busy ? "Saving…" : "Save All Changes"}
      </button>
    </div>
  );
}

function SalesTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/sales").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const resend = async (t) => {
    try {
      await api.post(`/admin/sales/${t.id}/resend-download`);
      toast.success(`Download email resent to ${t.customer_email}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const paid = (items || []).filter((t) => t.payment_status === "paid");
  const total = paid.reduce((s, t) => s + (t.amount || 0), 0) / 100;
  return (
    <div data-testid="admin-sales-tab">
      <div className="flex flex-wrap gap-6 mb-6">
        <div className="border border-gold/20 bg-gallery px-6 py-4">
          <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold/70">Paid Sales</p>
          <p className="font-serif text-3xl text-cream mt-1">{paid.length}</p>
        </div>
        <div className="border border-gold/20 bg-gallery px-6 py-4">
          <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold/70">Revenue</p>
          <p className="font-serif text-3xl text-cream mt-1">${total.toFixed(2)}</p>
        </div>
      </div>
      <div className="border border-gold/15 overflow-x-auto">
        <table className="w-full text-sm" data-testid="sales-table">
          <thead>
            <tr className="border-b border-gold/15 text-left">
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Song</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Amount</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Status</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Downloads</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70">Date</th>
              <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70"></th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((t) => (
              <tr key={t.id} className="border-b border-gold/10 text-sand">
                <td className="px-4 py-3 text-cream">{t.title}</td>
                <td className="px-4 py-3">${((t.amount || 0) / 100).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 font-mono text-[9px] tracking-[0.2em] uppercase ${
                    t.payment_status === "paid" ? "bg-gold/15 text-gold" : "bg-wine/30 text-cream/70"
                  }`}>
                    {t.payment_status}
                  </span>
                </td>
                <td className="px-4 py-3">{t.download_count || 0}</td>
                <td className="px-4 py-3 text-xs">{(t.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  {t.payment_status === "paid" && t.customer_email && (
                    <button
                      onClick={() => resend(t)}
                      data-testid={`resend-email-${t.id}`}
                      className="px-3 py-1.5 border border-gold/30 text-gold font-mono text-[9px] tracking-[0.2em] uppercase hover:bg-gold hover:text-obsidian transition-colors"
                    >
                      Resend Email
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sand/60">No sales yet — turn on "Sell as download" on any Vault release.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TABS = [
  { id: "vault", label: "Royal Vault" },
  { id: "official", label: "Official Music" },
  { id: "content", label: "Videos & Content" },
  { id: "site", label: "Pages & Images" },
  { id: "subscribers", label: "Royal List" },
  { id: "sales", label: "Sales" },
  { id: "activity", label: "Activity" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("vault");
  useSEO("Royal Office", "Queen Denae admin.");

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-xs tracking-[0.3em] uppercase text-sand">Checking access…</div>;
  }
  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div data-testid="admin-cms-dashboard" className="pt-28 sm:pt-32 pb-24 min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-gold/70">The Royal Office</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-cream tracking-tight mt-1">Welcome, Queen.</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="h-11 px-5 inline-flex items-center border border-gold/25 text-sand font-mono text-[10px] tracking-[0.25em] uppercase hover:text-gold hover:border-gold transition-colors" data-testid="admin-view-site-btn">
              View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="h-11 px-5 inline-flex items-center gap-2 border border-wineaccent/50 text-wineaccent font-mono text-[10px] tracking-[0.25em] uppercase hover:bg-wineaccent hover:text-cream transition-colors"
            >
              <LogOut size={13} /> Log Out
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`h-11 px-5 font-mono text-[10px] tracking-[0.25em] uppercase border transition-colors ${
                tab === t.id ? "border-gold bg-gold text-obsidian" : "border-gold/20 text-sand hover:border-gold/60 hover:text-cream"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "vault" && (
          <CollectionTab
            endpoint="vault_releases"
            empty={EMPTY_VAULT}
            fields={vaultFields}
            testPrefix="admin-vault"
            extraRow={(i) => `· ${(i.play_count || 0).toLocaleString()} plays · /vault/${i.slug}`}
            onPreview={(i) => `/vault/${i.slug}`}
          />
        )}
        {tab === "official" && (
          <CollectionTab endpoint="official_releases" empty={EMPTY_OFFICIAL} fields={officialFields} testPrefix="admin-official" />
        )}
        {tab === "content" && (
          <CollectionTab endpoint="content_items" empty={EMPTY_CONTENT} fields={contentFields} testPrefix="admin-content" />
        )}
        {tab === "site" && <SiteTab />}
        {tab === "subscribers" && <SubscribersTab />}
        {tab === "sales" && <SalesTab />}
        {tab === "activity" && <ActivityTab />}
      </div>
    </div>
  );
}
